import { Injectable, Inject, Optional, RESPONSE_INIT, REQUEST_CONTEXT } from '@angular/core';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { environment } from '../../../environments/environment';

/**
 * This service is used to add so called "surrogate" headers to page responses
 * from the Angular SSR (server side rendering) run. It is mostly used inside API calls/services
 * to add information when embedding results from api calls on the page
 */
@Injectable({
  providedIn: 'root'
})
export class SurrogateKeyService {
  constructor(
    @Optional() @Inject(RESPONSE_INIT) private res: ResponseInit,
    @Optional() @Inject(REQUEST_CONTEXT) private requestContext,
    private platformService: PlatformInformationService
  ) {
    if (!this.platformService.getIsBrowser() && this.res) {
      const env = this.requestContext?.DOCKERENV ?? 'dev';
      this.addSurrogateKeys(['all', 'alpinresorts-angular', env]);
      if (this.res.headers instanceof Headers) {
        const maxAge = requestContext?.HEADER_MAX_AGE ?? '86400';
        const staleWhileRevalidate = requestContext?.STALE_WHILE_REV ?? '86400';
        this.res.headers.set(
          'Cache-Control',
          `public, max-age=${maxAge}, stale-while-revalidate=${staleWhileRevalidate}`
        );
      }
    }
  }

  addSurrogateKeys(keys: Array<string>) {
    if (!this.platformService.getIsBrowser() && this.res) {
      if (environment.surrogateKeyPrefixActive) {
        keys = keys.map(k => `frontend-${k}`);
      }
      // response_init type depends on the given runner. Node and cloudflare workers use 'real' headers.
      if (this.res.headers instanceof Headers) {
        const header: string = this.res?.headers.get('Surrogate-Key');
        if (header) {
          keys = header.split(' ').concat(keys);
        }
        // strip duplicate keys
        keys = this.stripDoublicates(keys);

        const joinedKeys = keys.join(' ').trim();
        this.res.headers.set('Surrogate-Key', joinedKeys);
        this.res.headers.set('Cache-Tag', joinedKeys);
      }
    }
  }

  /**
   * Strips double keys from a given array
   * @param keys string array that can contain doublicate entries
   */
  private stripDoublicates(keys: Array<string>): Array<string> {
    return keys.reduce(function (a, b) {
      if (a.indexOf(b) < 0) {
        a.push(b);
      }
      return a;
    }, []);
  }
}
